import React from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import { FormAccordion, FormAccordionItem } from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Grid } from '@mantine/core';
import { useFlags } from 'src/feature-flags/FlagsHook';
import {
  OWNER,
  OwnerSharesSection,
  resolveOwnersPayload,
} from 'src/financial/financial-data/financial-item/forms/common/OwnerSharesSection';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { FormTitle } from 'src/financial/financial-data/financial-item/forms/common/FormTitle';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { useJointOwnerDefault } from 'src/financial/financial-data/financial-item/forms/common/useJointOwnerDefault';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import {
  BENEFICIARY,
  BeneficiaryFormAccordionItemList,
} from 'src/financial/financial-data/financial-item/forms/common/BeneficiaryFormAccordionItemList';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import { ValuationAccordionItem } from 'src/financial/financial-data/financial-item/rework/ValuationAccordionItem';
import { VALUATION_SECTION } from 'src/financial/financial-data/financial-item/rework/financialItemRework';

const PROPERTY_PRODUCT = 'Property';

export function Property(props: ProductFormComponentProps) {
  const { onSave } = props;
  const { flagEnabledFinancialItemOwners } = useFlags();
  const { isCreateMode, uiData, initialValues, onDeleteConfirm, feedInfo, onConnectAsset } =
    getFinancialFormSetupValues(PROPERTY_PRODUCT, props, [
      { product: BENEFICIARY, field: 'beneficiaries' },
      { product: OWNER, field: 'owners' },
    ]);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
  });

  useJointOwnerDefault(form, PROPERTY_PRODUCT, isCreateMode);

  const handleSave = async (values: FormValuesType) => {
    if (!(BENEFICIARY in values && Array.isArray(values[BENEFICIARY]))) return;

    const ownersPayload = resolveOwnersPayload(
      values,
      PROPERTY_PRODUCT,
      flagEnabledFinancialItemOwners,
    );
    if (ownersPayload === null) return;

    const mappedValues = {
      ...values[PROPERTY_PRODUCT],
      beneficiaries: values[BENEFICIARY].map(item => ({ ...item })),
      ...ownersPayload,
    };

    await onSave(mappedValues, uiData.ui.entitiesMetadata[PROPERTY_PRODUCT]);
  };

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave)} autoComplete={'off'}>
        <FormContent
          form={form}
          feedInfo={feedInfo}
          onConnect={onConnectAsset}
          showOwners={flagEnabledFinancialItemOwners}
        />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
  showOwners: boolean;
};

const FormContent = ({ form, feedInfo, onConnect, showOwners }: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  const { flagEnabledFinancialRework } = useFlags();
  return (
    <FinancialFormContainer
      product={PROPERTY_PRODUCT}
      title={<FormTitle title={'Assets > Property'} product={PROPERTY_PRODUCT} />}
      feedInfo={feedInfo}
      onConnect={onConnect}
    >
      <FormAccordion defaultValue={[VALUATION_SECTION, 'summary', 'info', 'beneficiaries']}>
        <ValuationAccordionItem product={PROPERTY_PRODUCT} />
        <FormAccordionItem value="summary" label="Summary">
          <Grid>
            <GridPair
              first={<FinancialFormField label="Type" field="type" product={PROPERTY_PRODUCT} />}
              second={<FinancialFormField field="description" product={PROPERTY_PRODUCT} />}
            />
            <GridPair first={<FinancialFormField field="address" product={PROPERTY_PRODUCT} />} />
          </Grid>
          <Grid>
            <GridPair
              isHighlighted
              first={<FinancialFormField field="purchase_price" product={PROPERTY_PRODUCT} />}
              second={<FinancialFormField field="date_of_purchase" product={PROPERTY_PRODUCT} />}
            />
            {!showOwners && !flagEnabledFinancialRework && (
              <GridPair
                first={<FinancialFormField field="is_joint" product={PROPERTY_PRODUCT} />}
              />
            )}
            <GridPair
              fullWidth
              first={<FinancialFormField field="notes" product={PROPERTY_PRODUCT} />}
            />
          </Grid>
        </FormAccordionItem>
        {!flagEnabledFinancialRework && (
          <FormAccordionItem value="valuation" label="Valuation">
            <Grid>
              <GridPair
                isHighlighted
                first={<FinancialFormField field="value" product={PROPERTY_PRODUCT} />}
                second={<FinancialFormField field="value_date" product={PROPERTY_PRODUCT} />}
              />
            </Grid>
            {showOwners && <OwnerSharesSection product={PROPERTY_PRODUCT} />}
          </FormAccordionItem>
        )}
        <FormAccordionItem value="info" label="Additional information">
          <Grid>
            <GridPair
              first={<FinancialFormField field="is_held_in_trust" product={PROPERTY_PRODUCT} />}
              second={<FinancialFormField field="intended_use" product={PROPERTY_PRODUCT} />}
            />
          </Grid>
        </FormAccordionItem>
        <BeneficiaryFormAccordionItemList form={form} isEditing={isEditing} />
      </FormAccordion>
    </FinancialFormContainer>
  );
};
